/**
 * Tenants admin API client - typed wrappers over /v1/super-admin/tenants/*.
 * Caller must be platform_super_admin / platform_ops / platform_support.
 */

import { apiFetch } from './api';

export type TenantOrigin = 'core_bootstrap' | 'self_provisioned' | 'core_attached';
export type TenantStatus = 'provisioning' | 'active' | 'paused' | 'terminating';

export interface Tenant {
  id: string;
  slug: string;
  name: string;
  origin: TenantOrigin;
  coreTenantId: string | null;
  status: TenantStatus;
  industryHints: string[];
  regulations: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ListTenantsResponse {
  tenants: Tenant[];
  pagination: { total: number; limit: number; offset: number };
}

export interface CreateTenantInput {
  id?: string;
  slug: string;
  name: string;
  industryHints?: string[];
  regulations?: string[];
  // Slice 2.3 - wizard module selection + bundle picker.
  modules?: string[];
  sku?:
    | 'bfsi_enterprise'
    | 'healthcare_enterprise'
    | 'enterprise'
    | 'professional'
    | 'starter'
    | 'msme_light'
    | 'consent_only'
    | 'auditor_read_only';
  dataPlaneMode?:
    | 'shared_rls'
    | 'dedicated_db_shared_cluster'
    | 'dedicated_cluster'
    | 'in_customer_vpc';
}

export interface UpdateTenantInput {
  name?: string;
  industryHints?: string[];
  regulations?: string[];
  status?: TenantStatus;
}

export async function listTenants(query: {
  search?: string;
  origin?: TenantOrigin;
  status?: TenantStatus;
  limit?: number;
  offset?: number;
} = {}): Promise<ListTenantsResponse> {
  const params = new URLSearchParams();
  if (query.search) params.set('search', query.search);
  if (query.origin) params.set('origin', query.origin);
  if (query.status) params.set('status', query.status);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.offset !== undefined) params.set('offset', String(query.offset));
  const qs = params.toString();
  return apiFetch<ListTenantsResponse>(`/v1/super-admin/tenants${qs ? '?' + qs : ''}`);
}

export async function getTenant(id: string): Promise<Tenant> {
  return apiFetch<Tenant>(`/v1/super-admin/tenants/${encodeURIComponent(id)}`);
}

export async function createTenant(input: CreateTenantInput): Promise<Tenant> {
  return apiFetch<Tenant>('/v1/super-admin/tenants', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateTenant(id: string, patch: UpdateTenantInput): Promise<Tenant> {
  return apiFetch<Tenant>(`/v1/super-admin/tenants/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function terminateTenant(id: string): Promise<Tenant> {
  return apiFetch<Tenant>(`/v1/super-admin/tenants/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export const ORIGIN_META: Record<TenantOrigin, { label: string; tone: 'emerald' | 'cyan' | 'amber' }> = {
  self_provisioned: { label: 'Self-provisioned', tone: 'emerald' },
  core_bootstrap: { label: 'Core bootstrap', tone: 'cyan' },
  core_attached: { label: 'Core attached', tone: 'cyan' },
};

export const STATUS_META: Record<TenantStatus, { label: string; tone: 'success' | 'warning' | 'critical' | 'glass' }> = {
  provisioning: { label: 'Provisioning', tone: 'warning' },
  active: { label: 'Active', tone: 'success' },
  paused: { label: 'Paused', tone: 'warning' },
  terminating: { label: 'Terminating', tone: 'critical' },
};

// ─── Customer deployment pack ────────────────────────────────────────

export type TlsMode = 'internal-ca' | 'lets-encrypt' | 'behind-load-balancer';

export interface PackFile {
  filename: string;
  purpose: string;
  contents: string;
  sha256: string;
}

export interface DeploymentPack {
  tenantId: string;
  slug: string;
  tenantName: string;
  version: string;
  siteAddress: string;
  tls: TlsMode;
  generatedAt: string;
  files: PackFile[];
  manifest: string;
  /** What the pack deliberately excludes - shown so nobody expects images in it. */
  notIncluded: string[];
}

export interface GeneratePackInput {
  siteAddress: string;
  version: string;
  tls: TlsMode;
  registry?: string;
}

/**
 * Generate the on-prem / air-gapped install pack for one tenant.
 *
 * Returns file CONTENTS, not a zip: the payload is kilobytes, and keeping it as
 * JSON lets the page show each file before anyone downloads anything.
 */
export async function generateDeploymentPack(
  tenantId: string,
  input: GeneratePackInput,
): Promise<DeploymentPack> {
  return apiFetch<DeploymentPack>(
    `/v1/super-admin/tenants/${encodeURIComponent(tenantId)}/deployment-pack`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}
